import type { ReactNode } from 'react'
import { toast } from 'react-hot-toast'
import { trpc } from '@/lib/trpc'
import type {
  DashboardExperience,
  RolloutStatus as RolloutStatusData,
} from '../../../../api/_trpc/routers/admin/coaching-v2/rollout'
import { SectionCard } from './shared/section-card'

function formatTimestamp(value: Date | string | null): string {
  if (value === null) return '—'
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`
}

function formatAge(ms: number | null): string {
  if (ms === null) return '—'
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes === 0) return `${seconds}s`
  const hours = Math.floor(minutes / 60)
  if (hours === 0) return `${minutes}m ${seconds}s`
  return `${hours}h ${minutes % 60}m`
}

const EXPERIENCE_LABEL: Record<DashboardExperience, string> = {
  classic: 'Classic',
  v2: 'Coaching v2',
}

function Metric(props: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--cd-text-muted)">
        {props.label}
      </span>
      <span className="text-sm font-semibold text-(--cd-text)">
        {props.value}
      </span>
    </div>
  )
}

/** tRPC serializes Date fields to strings over the wire; accept either form. */
export type RolloutStatusForDisplay = Omit<
  RolloutStatusData,
  | 'cutoverAt'
  | 'lastRollbackAt'
  | 'lastSuccessfulSweepAt'
  | 'lastSuccessfulReconciliationAt'
> & {
  cutoverAt: Date | string | null
  lastRollbackAt: Date | string | null
  lastSuccessfulSweepAt: Date | string | null
  lastSuccessfulReconciliationAt: Date | string | null
}

export interface RolloutStatusViewProps {
  status: RolloutStatusForDisplay
  onSwitchExperience: (target: DashboardExperience) => void
  onToggleNotion: (enabled: boolean) => void
  switching: boolean
  togglingNotion: boolean
}

/** Pure presentation of the authoritative rollout status (spec Task 16 §4). */
export function RolloutStatusView(props: RolloutStatusViewProps): ReactNode {
  const { status } = props
  const nextExperience: DashboardExperience =
    status.dashboardExperience === 'v2' ? 'classic' : 'v2'

  return (
    <SectionCard title="Rollout & delivery">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Metric
            label="Experience"
            value={EXPERIENCE_LABEL[status.dashboardExperience]}
          />
          <button
            type="button"
            onClick={() => props.onSwitchExperience(nextExperience)}
            disabled={props.switching}
            className="rounded-md bg-(--cd-tab-active) px-3 py-1.5 text-xs font-medium text-(--cd-text) disabled:opacity-50"
          >
            Switch to {EXPERIENCE_LABEL[nextExperience]}
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Metric
            label="First cutover"
            value={formatTimestamp(status.cutoverAt)}
          />
          <Metric
            label="Last rollback"
            value={formatTimestamp(status.lastRollbackAt)}
          />
          <Metric
            label="Canonical assignments"
            value={status.canonicalAssignmentCount}
          />
          <Metric
            label="Preexisting legacy"
            value={status.preexistingLegacyCount}
          />
          <Metric
            label="Last sweep"
            value={formatTimestamp(status.lastSuccessfulSweepAt)}
          />
          <Metric
            label="Last reconciliation"
            value={formatTimestamp(status.lastSuccessfulReconciliationAt)}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-(--cd-inset) p-3">
          <Metric
            label="Notion delivery"
            value={
              <span
                data-testid="notion-delivery-enablement"
                className={
                  status.notionDeliveryEnabled
                    ? 'text-(--cd-green)'
                    : 'text-(--cd-text-muted)'
                }
              >
                {status.notionDeliveryEnabled ? 'Enabled' : 'Disabled'}
              </span>
            }
          />
          <button
            type="button"
            onClick={() => props.onToggleNotion(!status.notionDeliveryEnabled)}
            disabled={
              props.togglingNotion ||
              (!status.notionDeliveryEnabled && !status.notionConnectionActive)
            }
            title={
              !status.notionDeliveryEnabled && !status.notionConnectionActive
                ? 'Run the connection preflight before enabling Notion delivery'
                : undefined
            }
            className="rounded-md border border-(--cd-border) px-3 py-1.5 text-xs font-medium text-(--cd-text) disabled:opacity-50"
          >
            {status.notionDeliveryEnabled ? 'Disable' : 'Enable'} Notion
            delivery
          </button>
        </div>

        <div>
          <span className="text-[11px] font-medium uppercase tracking-wide text-(--cd-text-muted)">
            Delivery queue
          </span>
          <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Metric label="Queued" value={status.delivery.queued} />
            <Metric label="Creating" value={status.delivery.creating} />
            <Metric label="Failed" value={status.delivery.failed} />
            <Metric label="Unknown" value={status.delivery.unknown} />
            <Metric
              label="Stale creating age"
              value={formatAge(status.staleCreatingAgeMs)}
            />
          </div>
        </div>
      </div>
    </SectionCard>
  )
}

/** Container: reads authoritative status and persists rollout changes. */
export function RolloutStatus(props: { clientId: string }): ReactNode {
  const utils = trpc.useUtils()
  const statusQuery = trpc.admin.coachingV2.rollout.getStatus.useQuery({
    clientId: props.clientId,
  })

  async function refreshAfterWrite(): Promise<void> {
    await Promise.all([
      utils.admin.coachingV2.rollout.getStatus.invalidate({
        clientId: props.clientId,
      }),
      utils.admin.clients.getById.invalidate(),
    ])
  }

  const setExperience =
    trpc.admin.coachingV2.rollout.setDashboardExperience.useMutation({
      onSuccess: async (state) => {
        toast.success(
          state.dashboardExperience === 'v2'
            ? 'Switched to Coaching v2'
            : 'Rolled back to Classic',
        )
        await refreshAfterWrite()
      },
      onError: (error) => toast.error(error.message),
    })

  const setNotion =
    trpc.admin.coachingV2.rollout.setNotionDeliveryEnabled.useMutation({
      onSuccess: async (state) => {
        toast.success(
          state.notionDeliveryEnabled
            ? 'Notion delivery enabled'
            : 'Notion delivery disabled',
        )
        await refreshAfterWrite()
      },
      onError: (error) => toast.error(error.message),
    })

  if (statusQuery.isLoading) {
    return (
      <SectionCard title="Rollout & delivery">
        <p className="text-sm text-(--cd-text-muted)">
          Loading rollout status…
        </p>
      </SectionCard>
    )
  }
  if (statusQuery.error || !statusQuery.data) {
    return (
      <SectionCard title="Rollout & delivery">
        <p className="text-sm text-(--cd-red)" role="alert">
          {statusQuery.error?.message ?? 'Rollout status is unavailable'}
        </p>
      </SectionCard>
    )
  }

  const status = statusQuery.data
  return (
    <RolloutStatusView
      status={status}
      switching={setExperience.isPending}
      togglingNotion={setNotion.isPending}
      onSwitchExperience={(target) =>
        setExperience.mutate({
          clientId: props.clientId,
          experience: target,
          expectedVersion: status.version,
        })
      }
      onToggleNotion={(enabled) =>
        setNotion.mutate({
          clientId: props.clientId,
          enabled,
          expectedVersion: status.version,
        })
      }
    />
  )
}
